import { Fragment, Ref } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/20/solid';
import {
  ArrowsRightLeftIcon,
  DocumentTextIcon as FileIcon,
  GlobeAltIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useWindowSize } from 'usehooks-ts';

import DefaultProfilePicture from '@/assets/images/DefaultProfilePicture.png';
import { ReactComponent as DefaultHeaderImage } from '@/assets/svg/ProfilePageDefaultHeaderImage.svg';
import { ReactComponent as BlankProfileAvatar } from '@/assets/svg/default_avatar.svg';
import { isFileLink } from '@/helpers/file';
import {
  formatPhoneNumber,
  getFullCompanyAddress,
  getFullName,
  hexToRgba,
} from '@/helpers/strings';
import useAppState from '@/hooks/useAppState';
import IFile from '@/types/IFile';
import {
  ExtraEmail,
  ExtraMobileNumber,
  IProfileFileLink,
  IProfileQuickLink,
  IProfileSocialLink,
} from '@/types/IProfile';

import { ProgressiveImage } from './Image';
import { mockProfileData } from './ProfilePage/data';
import { ProfileSocialLinks } from './SocialLinks';

type ProfileContent = {
  additional_information?: (ExtraEmail | ExtraMobileNumber)[];
  company_name?: string | null;
  company_website?: string | null;
  company_phone_number?: string | null;
  country?: string | null;
  department?: string | null;
  email?: string | null;
  extra_company_phone_numbers?: string[] | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  job_title?: string | null;
  mobile_number?: string | null;
  note?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  post_code?: string | null;
  profile_social_links?: IProfileSocialLink[];
  profile_file_links?: IProfileFileLink[];
  profile_quick_links?: IProfileQuickLink[];
  pronouns?: string | null;
  photo?: IFile | null;
};

type ProfilePreviewModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  settings: {
    bg_color: string;
    text_color: string;
    button_bg_color: string;
    button_text_color: string;
    logo: IFile | null;
    logo_file_id: number | null;
  };
  content?: ProfileContent;
};

export function ProfilePreviewModal({
  isOpen,
  setIsOpen,
  settings,
  content,
}: ProfilePreviewModalProps) {
  const { multipleProfiles } = useAppState();

  return (
    <Dialog
      as="div"
      className="fixed inset-0 z-10 overflow-y-auto"
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Transition appear show={isOpen}>
        <div
          className="flex flex-col space-y-2 items-center justify-center"
          style={{
            height: '100dvh',
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-300 bg-opacity-40" />
          </Transition.Child>

          <div className="w-[320px] md:w-[356px] flex justify-end">
            <button
              type="button"
              className="rounded-full bg-white p-1 w-min self-end z-10"
              onClick={() => setIsOpen(false)}
            >
              <XMarkIcon className="w-8 h-8 text-gray-500" />
            </button>
          </div>
          {multipleProfiles.length > 0 && (
            <div className="py-1 px-3 text-sm rounded-full bg-brand-100 text-brand-500 w-fit place-self-center mb-4">
              Showing profile 1/{multipleProfiles.length}
            </div>
          )}
          <Transition.Child
            as={Fragment}
            enter="ease-in duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-out duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <ProfilePreview settings={settings} content={content} />
          </Transition.Child>
        </div>
      </Transition>
    </Dialog>
  );
}

type ProfilePreviewProps = {
  ref?: Ref<HTMLDivElement>;
  content?: ProfileContent;
  settings: {
    bg_color: string;
    text_color: string;
    button_bg_color: string;
    button_text_color: string;
    logo: IFile | null;
    logo_file_id: number | null;
  };
  showDefaultProfilePicture?: boolean;
};

export function ProfilePreview({
  ref,
  content,
  settings,
  showDefaultProfilePicture = true,
}: ProfilePreviewProps) {
  const { height } = useWindowSize();

  const {
    bg_color: bgColor,
    text_color: textColor,
    button_bg_color: buttonBgColor,
    button_text_color: buttonTextColor,
    logo,
  } = settings;
  const {
    additional_information: additionalInformation,
    company_name: companyName,
    company_phone_number: companyPhoneNumber,
    company_website: companyWebsite,
    country,
    city,
    department,
    email,
    first_name: firstName,
    job_title: jobTitle,
    last_name: lastName,
    mobile_number: mobileNumber,
    middle_name: middleName,
    note,
    photo,
    profile_social_links: profileSocialLinks,
    profile_file_links: profileFileLinks,
    profile_quick_links: profileQuickLinks,
    pronouns,
    post_code: postCode,
    state,
    street_address: streetAddress,
  } = content ? content : mockProfileData;

  const fullName = getFullName({ firstName, middleName, lastName });
  const jobDetails = [
    { key: 'jobTitle', value: jobTitle },
    { key: 'department', value: department },
  ];
  const formattedPhoneNumber = formatPhoneNumber(mobileNumber);
  const links = [
    ...(profileFileLinks || []),
    ...(profileQuickLinks || []),
  ].sort((a, b) => a.order - b.order);
  const sortedSocialLinks = [...(profileSocialLinks || [])].sort(
    (a, b) => a.order - b.order,
  );

  const hasSocials = sortedSocialLinks.length > 0;
  const hasLinks = links.length > 0;
  const hasCompanyName = !!companyName;
  const hasCompanyPhoneNumber = !!companyPhoneNumber;
  const hasCompanyWebsite = !!companyWebsite;
  const hasCompanyAddress =
    !!streetAddress || !!city || !!state || !!postCode || !!country;
  const hasCompanyInformation =
    hasCompanyName ||
    hasCompanyPhoneNumber ||
    hasCompanyWebsite ||
    hasCompanyAddress;

  const hasAdditionalContactDetails =
    additionalInformation && additionalInformation.length > 0;

  return (
    <div
      ref={ref}
      className="transition-all text-2xl transform shadow-xl rounded-4xl text-left overflow-scroll hide-scrollbar w-full max-w-[320px] md:max-w-[356px] max-h-[625px] md:max-h-[695px]"
      style={{
        backgroundColor: bgColor,
      }}
    >
      <div className="flex-1 pb-4">
        <div
          className="w-full absolute max-h-[265px] overflow-hidden"
          style={{ height: height * 0.45 }}
        >
          {logo ? (
            <img
              src={logo.small_url}
              className="w-full h-full"
              alt="Cover image"
            />
          ) : (
            <div>
              <DefaultHeaderImage className="w-full h-full" />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div
            className="w-full max-h-[265px]"
            style={{ height: height * 0.45 }}
          />
          <div
            className="rounded-tr-4xl p-4 -mt-8 relative"
            style={{ color: textColor, backgroundColor: bgColor }}
          >
            <div className="-mt-16">
              <div
                className="rounded-full w-[88px] h-[88px] flex items-center justify-center overflow-hidden relative"
                style={{
                  backgroundColor: bgColor,
                }}
              >
                {photo ? (
                  <ProgressiveImage
                    source={photo.thumb_url}
                    blurHash={photo.blur_hash}
                    alt="Profile photo"
                  />
                ) : showDefaultProfilePicture ? (
                  <img src={DefaultProfilePicture} alt="Profile photo" />
                ) : (
                  <BlankProfileAvatar
                    width={88}
                    height={88}
                    className="rounded-full"
                  />
                )}
              </div>
              {(fullName || pronouns) && (
                <div className="font-semibold text-[28px] pt-4">
                  {fullName}
                  {pronouns && (
                    <span
                      className={`text-xs font-normal ${fullName && 'pl-1.5'}`}
                      style={{ color: hexToRgba(textColor, 0.8) }}
                    >
                      {pronouns}
                    </span>
                  )}
                </div>
              )}
              {jobDetails
                .filter(item => !!item.value)
                .map((item, index) => (
                  <div
                    className={index === 0 ? 'text-lg pt-1' : 'text-base'}
                    style={{ color: textColor }}
                  >
                    {item.value}
                  </div>
                ))}
              <div className="h-[8px]" />
              {formattedPhoneNumber && (
                <div
                  className="font-normal text-base"
                  style={{ color: textColor }}
                >
                  {formattedPhoneNumber}
                </div>
              )}
              <div className="space-y-6">
                {email && (
                  <div
                    className="font-normal text-base truncate"
                    style={{ color: textColor }}
                  >
                    {email}
                  </div>
                )}
                {hasSocials && (
                  <ProfileSocialLinks
                    links={sortedSocialLinks}
                    textColor={textColor}
                  />
                )}
                {hasLinks && (
                  <div className="space-y-3">
                    <span className="text-lg font-semibold">Links</span>
                    {links.map((link, index) => {
                      const { title } = link;

                      const Icon = isFileLink(link) ? FileIcon : LinkIcon;

                      return (
                        <div
                          key={index}
                          className="border rounded-full flex items-center justify-between text-base px-6 py-3 font-normal"
                          style={{
                            borderColor: hexToRgba(textColor, 0.5),
                            color: textColor,
                          }}
                        >
                          {title}
                          <Icon className="flex-none w-5 h-5" />
                        </div>
                      );
                    })}
                  </div>
                )}
                {hasCompanyInformation && (
                  <div className="w-full">
                    <div
                      className="font-medium text-lg pb-2"
                      style={{
                        color: textColor,
                      }}
                    >
                      {companyName || 'Company Information'}
                    </div>
                    {hasCompanyWebsite && (
                      <div
                        className="flex items-center py-2 text-base gap-x-3 border-b font-normal"
                        style={{
                          color: textColor,
                          borderColor: hexToRgba(textColor, 0.1),
                        }}
                      >
                        <GlobeAltIcon
                          className="w-5 h-5 flex-shrink-0"
                          color={hexToRgba(textColor, 0.35)}
                        />
                        {companyWebsite}
                      </div>
                    )}
                    {hasCompanyPhoneNumber && (
                      <div
                        className="flex items-center py-2 text-base gap-x-3 border-b font-normal"
                        style={{
                          color: textColor,
                          borderColor: hexToRgba(textColor, 0.1),
                        }}
                      >
                        <PhoneIcon
                          className="w-5 h-5 flex-shrink-0"
                          color={hexToRgba(textColor, 0.35)}
                        />
                        {companyPhoneNumber}
                      </div>
                    )}
                    {hasCompanyAddress && (
                      <div
                        className="flex py-2 text-base gap-x-3 font-normal border-b"
                        style={{
                          color: textColor,
                          borderColor: hexToRgba(textColor, 0.1),
                        }}
                      >
                        <MapPinIcon
                          className="w-5 h-5 flex-shrink-0"
                          color={hexToRgba(textColor, 0.35)}
                        />
                        <span className="whitespace-pre-line">
                          {getFullCompanyAddress({
                            streetAddress,
                            city,
                            state,
                            postCode,
                            country,
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {hasAdditionalContactDetails && (
                  <div className="w-full">
                    <div
                      className="font-medium text-lg"
                      style={{
                        color: textColor,
                      }}
                    >
                      Other contact details
                      {additionalInformation.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center border-b py-2 space-x-3"
                          style={{
                            color: textColor,
                            borderColor: hexToRgba(textColor, 0.1),
                          }}
                        >
                          {item.type === 'email' ? (
                            <EnvelopeIcon
                              className="w-5 h-5 flex-shrink-0"
                              color={hexToRgba(textColor, 0.35)}
                            />
                          ) : (
                            <PhoneIcon
                              className="w-5 h-5 flex-shrink-0"
                              color={hexToRgba(textColor, 0.35)}
                            />
                          )}
                          <div className="flex flex-col">
                            <span
                              className="text-base font-normal"
                              style={{ color: textColor }}
                            >
                              {item.value}
                            </span>
                            <span
                              className="text-xs font-normal"
                              style={{ color: hexToRgba(textColor, 0.5) }}
                            >
                              {item.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {note && (
                  <div className="flex flex-col space-y-3">
                    <span className="text-lg font-medium">Note</span>
                    <p
                      className="whitespace-pre-wrap text-base"
                      style={{ color: hexToRgba(textColor, 0.8) }}
                    >
                      {note}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="sticky bottom-4 mx-4 h-12 rounded-full flex items-center justify-center gap-1 text-base"
        style={{
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
        }}
      >
        Save contact
        <ArrowsRightLeftIcon width={20} height={20} strokeWidth={2} />
      </div>
    </div>
  );
}
